import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useMemo, useState } from 'react';
import cn from 'classnames';
import { useDevice } from '../../providers/Device';
import { Autocomplete } from '../Autocomplete/Autocomplete';
import { useBiAction } from '../BiTracking';
import { Checkbox } from '../Checkbox/Checkbox';
import styles from './MultiAutocomplete.module.scss';
import { GetMultiAutocompleteTID } from './MultiAutocomplete.utils';
import { MultiAutocompleteChips } from './MultiAutocompleteChips';
import { MultiAutocompleteMobile } from './MultiAutocompleteMobile';
export function MultiAutocomplete(props) {
    const { options, value, onChange, getOptionId, getOptionLabel, getOptionDisabled, renderOption, onDraftChange, isLoading, isError, errorText, label, placeholder, noOptionsText, chips = 'fit', testId, bi } = props;
    const { MultiAutocompleteTestIds } = useMemo(()=>GetMultiAutocompleteTID(testId), [
        testId
    ]);
    const logBiAction = useBiAction();
    const { isMobileDevice } = useDevice();
    const resolvedTestId = testId !== null && testId !== void 0 ? testId : '';
    const [inputValue, setInputValue] = useState('');
    const isSelected = (option)=>value.some((o)=>getOptionId(o) === getOptionId(option));
    const logOptionBi = (optionId, wasSelected)=>{
        if (bi === null || bi === void 0 ? void 0 : bi.name) {
            logBiAction({
                entityType: 'multi_autocomplete',
                entityName: bi.name,
                actionType: wasSelected ? 'off' : 'on',
                data: _object_spread_props(_object_spread({}, bi.data), {
                    option_id: optionId,
                    test_id: resolvedTestId
                })
            });
        }
    };
    const toggle = (option)=>{
        const wasSelected = isSelected(option);
        logOptionBi(getOptionId(option), wasSelected);
        const next = wasSelected ? value.filter((o)=>getOptionId(o) !== getOptionId(option)) : [
            ...value,
            option
        ];
        onChange(onDraftChange ? onDraftChange(next, option) : next);
    };
    const removeChip = (id)=>{
        logOptionBi(id, true);
        onChange(value.filter((o)=>getOptionId(o) !== id));
    };
    if (isMobileDevice) {
        return /*#__PURE__*/ _jsx(MultiAutocompleteMobile, _object_spread({}, props));
    }
    return /*#__PURE__*/ _jsx(Autocomplete, {
        className: cn(styles.field, {
            [styles.wrapField]: chips === 'wrap'
        }),
        // eslint-disable-next-line custom/no-hardcoded-nobi-in-seagull -- WHY: MultiAutocomplete emits its own multi_autocomplete BI (option on/off in toggle, select in apply); instrumenting the wrapped Autocomplete would double-fire.
        noBi: true,
        multiple: true,
        disableCloseOnSelect: true,
        disableSelectedHighlight: true,
        testId: MultiAutocompleteTestIds.Root(),
        options: options,
        value: value,
        noOptionsText: noOptionsText !== null && noOptionsText !== void 0 ? noOptionsText : 'No options',
        disabled: isLoading,
        loading: isLoading,
        error: isError,
        helperText: isError ? errorText : undefined,
        getOptionLabel: getOptionLabel,
        getOptionDisabled: (option)=>{
            var _ref;
            return (_ref = getOptionDisabled === null || getOptionDisabled === void 0 ? void 0 : getOptionDisabled(option, value)) !== null && _ref !== void 0 ? _ref : false;
        },
        inputValue: inputValue,
        onInputChange: (_event, nextInputValue)=>setInputValue(nextInputValue),
        onOpen: ()=>setInputValue(''),
        onChange: (_event, _changedValue, reason, detail)=>{
            if (reason === 'clear') {
                onChange([]);
                return;
            }
            if (reason !== 'select-option' && reason !== 'remove-option') return;
            if (detail === null || detail === void 0 ? void 0 : detail.option) toggle(detail.option);
        },
        renderOption: (option)=>{
            const selected = isSelected(option);
            return renderOption ? renderOption(option, {
                selected
            }) : /*#__PURE__*/ _jsx(Checkbox, {
                // eslint-disable-next-line custom/no-hardcoded-nobi-in-seagull -- WHY: toggle() logs the option on/off BI; instrumenting this Checkbox would double-fire.
                noBi: true,
                checked: selected,
                label: getOptionLabel(option),
                testId: MultiAutocompleteTestIds.Option(getOptionId(option))
            });
        },
        renderTags: (committed)=>/*#__PURE__*/ _jsx(MultiAutocompleteChips, {
                labels: committed.map(getOptionLabel),
                ids: committed.map(getOptionId),
                onRemove: removeChip,
                wrap: chips === 'wrap',
                chipTestId: MultiAutocompleteTestIds.Chip,
                overflowTestId: MultiAutocompleteTestIds.ChipOverflow()
            }),
        textFieldProps: {
            label,
            placeholder: value.length > 0 ? '' : placeholder,
            alwaysShrinkLabel: true
        }
    });
}
